import { NgClass, NgIf, NgForOf, UpperCasePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { AppSettings } from '../../app.settings';
import { Subscription } from 'rxjs';
import { AdminInterface } from '@App/common/interfaces';
import { UserModel } from '@App/common/models';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { NoLeadingSpaceDirective } from '../../../common/directives/no-leading-space.directive';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-assign-user-list',
  templateUrl: './assign-users.component.html',
  styleUrls: ['./assign-users.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TooltipModule,
    NoLeadingSpaceDirective,
    InputTextModule,
    TranslateModule,
    DialogModule,
    InputNumberModule,
    NgClass,
    NgIf,
    NgForOf,
    UpperCasePipe
  ],
  encapsulation: ViewEncapsulation.None
})
export class AssignUsersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() assignedUsers: AdminInterface[];
  @Input() superAdminId: number;
  @Input() errorsConfig: any[] = [];

  @Output() saveChange = new EventEmitter<UserModel[]>();
  @Output() deleteUsers = new EventEmitter<UserModel[]>();

  @ViewChild('userContainer') userContainer: ElementRef;

  form: UntypedFormGroup;
  inputItems: UntypedFormArray;
  $assignUserSubscribe = new Subscription();
  isSubmit: boolean = false;
  addAdditionalAdminsModal: boolean = false;

  errorMessage: string = '';
  additionalNumberOfAdmins: number = 2;

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.formInit();
    this.setInputItems();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.assignedUsers?.currentValue && this.inputItems) {
      this.updateInputs(changes.assignedUsers.currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this.$assignUserSubscribe) {
      this.$assignUserSubscribe.unsubscribe();
    }
  }

  formInit() {
    this.form = this.fb.group({
      inputItems: this.fb.array([])
    });

    this.inputItems = this.form.get('inputItems') as UntypedFormArray;
  }

  setInputItems() {
    if (this.assignedUsers && this.assignedUsers.length > 0) {
      this.assignedUsers.map((adminObject) => {
        if (adminObject.user) {
          this.inputItems.push(
            this.fb.group(
              {
                emailId: new UntypedFormControl(
                  adminObject.user?.emailId,
                  Validators.pattern(AppSettings.EMAIL_PATTERN)
                ),
                id: new UntypedFormControl(
                  adminObject.user.id ? adminObject.user.id : 0
                ),
                userId: new UntypedFormControl(
                  adminObject.userId ? adminObject.userId : 0
                ),
                isCharged: new UntypedFormControl(true)
              },
              { updateOn: 'submit' }
            )
          );
        } else {
          this.setEmptyInput(true);
        }
      });
    }
  }

  setEmptyInput(isCharged: boolean) {
    this.inputItems.push(
      this.fb.group({
        emailId: new UntypedFormControl(
          '',
          Validators.pattern(AppSettings.EMAIL_PATTERN)
        ),
        id: new UntypedFormControl(''),
        userId: new UntypedFormControl(''),
        isCharged: new UntypedFormControl(isCharged)
      })
    );
  }

  updateInputs(assignedUsers: any[]) {
    const notPaidUsers: any[] = [];
    const paidUsers: any[] = [];

    assignedUsers.map((u) => {
      if (!u.user?.id) {
        notPaidUsers.push(u);
      } else {
        paidUsers.push(u);
      }
    });

    this.setNotPaidUsers(notPaidUsers);
    this.setPaidUsers(paidUsers);
    this.inputItems.controls.forEach((input) => {
      if (input.get('isCharged').value) {
        input.markAsPristine();
      }
    });
  }

  clearInput(user: any, index: number) {
    if (user.value?.userId) {
      this.deleteUsers.emit(user.value);
      this.inputItems.get(index.toString()).get('emailId').patchValue('');
    } else {
      this.inputItems.get(index.toString()).get('emailId').patchValue('');
    }
  }

  saveChanges(e: Event) {
    e.preventDefault();
    this.isSubmit = true;

    const duplicates = this.checkOnDuplicates();

    if (this.inputItems.valid && !duplicates.length) {
      const notPaidUsers: any[] = [];
      this.inputItems.controls.map((u) => {
        if (u.dirty) {
          notPaidUsers.push(u.value);
        }
      });
      this.saveChange.emit(notPaidUsers);
    }
  }

  checkOnDuplicates() {
    const emailValues = this.inputItems.value.map(
      ({ emailId }: { emailId: any }) => emailId
    );
    const duplicateValues: any[] = [];

    emailValues.some((item: any, idx: any) => {
      if (item && emailValues.indexOf(item) !== idx) {
        duplicateValues.push(item);
      }
    });

    if (duplicateValues.length) {
      this.inputItems.controls.map((control) => {
        if (
          duplicateValues.find((i) => i === control.get('emailId').value) !==
            undefined &&
          control.get('emailId').value
        ) {
          control.get('emailId').setErrors({ duplicate: true });
          control.get('emailId').markAsTouched();
          this.inputItems.controls[0].get('emailId').markAsUntouched();
        }
      });
    }
    return duplicateValues;
  }

  addMoreInputs() {
    const numberOfInputs =
      this.inputItems.length + this.additionalNumberOfAdmins;
    while (this.inputItems.length < numberOfInputs) {
      this.setEmptyInput(false);
    }

    this.addAdditionalAdminsModal = false;
  }

  setNotPaidUsers(notApprovedEmails: any[]) {
    notApprovedEmails.map(({ user }) => {
      if (
        this.inputItems.value.find(
          ({ emailId }: { emailId: any }) => emailId === user?.emailId
        )
      ) {
        this.inputItems.value.map((item: { emailId: any }, index: number) => {
          if (item.emailId === user?.user?.emailId) {
            this.inputItems
              .get(index.toString())
              .get('emailId')
              .setErrors({ failed: true });
            this.inputItems
              .get(index.toString())
              .get('isCharged')
              .patchValue(false);
            const errMessage = this.errorsConfig.find(
              (e) => e.body === item.emailId
            )?.message;
            this.errorMessage = errMessage ? errMessage : 'err_invalid_emailId';
          }
        });
      }
    });
  }

  setPaidUsers(paidUsers: any[]) {
    paidUsers.map((user) => {
      this.inputItems.value.map(
        ({ emailId }: { emailId: any }, index: number) => {
          if (emailId === user?.body && user?.status) {
            this.inputItems
              .get(index.toString())
              .get('isCharged')
              .patchValue(true);
          }
        }
      );
    });
  }

  setInputAsValid() {
    this.inputItems.controls.map((control) => {
      if (
        control.get('emailId').errors?.notPaid ||
        control.get('emailId').errors?.failed ||
        control.get('emailId').errors?.duplicate
      ) {
        control.get('emailId').setErrors(null);
        control.markAsDirty();
      }
      this.errorMessage = '';
    });
  }

  setInputValue(value: any, index: number) {
    this.inputItems.get(index.toString()).get('emailId').patchValue(value);
    this.inputItems.get(index.toString()).get('id').patchValue('');
    this.inputItems.get(index.toString()).get('isCharged').patchValue(false);
    this.inputItems.get(index.toString()).markAsDirty();
  }
}
